import React from 'react';
import { IonItem, IonLabel, IonInput, IonCheckbox } from '@ionic/react';
import { pxToRem } from '@/modules/config';
import SectionCard from '@/components/SectionCard';
import { globalIntroCapNotice } from '../intro_limit';

interface VisibilityLimitsBlockProps {
  byInviteOnly: boolean;
  setByInviteOnly: (value: boolean) => void;
  introLimitCountEnabled: boolean;
  setIntroLimitCountEnabled: (value: boolean) => void;
  introLimitCount: string;
  setIntroLimitCount: (value: string) => void;
  /** The global intro ceiling to surface as a notice, or null if none. */
  globalIntroMax: number | null;
  impressionGoal: string;
  setImpressionGoal: (value: string) => void;
  onlyManagersCanShare: boolean;
  setOnlyManagersCanShare: (value: boolean) => void;
}

/**
 * "Visibility & limits" block: by-invitation-only, per-topic invite cap (with
 * the global-ceiling notice), the impression goal, and the only-managers-can-share
 * toggle.
 */
const VisibilityLimitsBlock: React.FC<VisibilityLimitsBlockProps> = ({
  byInviteOnly,
  setByInviteOnly,
  introLimitCountEnabled,
  setIntroLimitCountEnabled,
  introLimitCount,
  setIntroLimitCount,
  globalIntroMax,
  impressionGoal,
  setImpressionGoal,
  onlyManagersCanShare,
  setOnlyManagersCanShare,
}) => (
  <SectionCard title="Visibility & limits">
    <IonItem lines="inset">
      <IonCheckbox
        slot="start"
        checked={byInviteOnly}
        onIonChange={e => setByInviteOnly(e.detail.checked)}
        data-testid="by-invite-only-checkbox"
      />
      <IonLabel style={{ whiteSpace: 'normal' }}>
        Visible by invitation only
      </IonLabel>
    </IonItem>

    <IonItem lines={globalIntroMax != null ? 'none' : 'inset'}>
      <IonCheckbox
        slot="start"
        checked={introLimitCountEnabled}
        onIonChange={e => setIntroLimitCountEnabled(e.detail.checked)}
        data-testid="intro-count-limit-checkbox"
      />
      <IonLabel style={{ whiteSpace: 'normal' }}>
        Restrict total intros to
      </IonLabel>
      <IonInput
        type="number"
        slot="end"
        value={introLimitCount}
        disabled={!introLimitCountEnabled}
        onIonInput={e => setIntroLimitCount((e.detail.value as string) || '')}
        placeholder="limit"
        style={{ maxWidth: '70px', textAlign: 'right' }}
        data-testid="intro-count-limit-input"
      />
    </IonItem>

    {globalIntroMax != null && (
      <IonItem
        lines="inset"
        style={
          {
            '--min-height': 'auto',
            '--padding-top': '0',
            '--padding-bottom': '8px',
            marginTop: '-8px',
          } as React.CSSProperties
        }
      >
        <IonLabel
          style={{
            whiteSpace: 'normal',
            fontSize: pxToRem(12),
            color: 'var(--ion-color-medium)',
            margin: 0,
          }}
          data-testid="global-intro-cap-notice"
        >
          {globalIntroCapNotice(globalIntroMax)}
        </IonLabel>
      </IonItem>
    )}

    <IonItem lines="inset">
      <IonLabel style={{ whiteSpace: 'normal' }}>
        Impression goal (# people to reach)
      </IonLabel>
      <IonInput
        type="number"
        slot="end"
        value={impressionGoal}
        onIonInput={e => setImpressionGoal((e.detail.value as string) || '')}
        placeholder="none"
        style={{ maxWidth: '70px', textAlign: 'right' }}
        data-testid="impression-goal-input"
      />
    </IonItem>

    <IonItem lines="none">
      <IonCheckbox
        slot="start"
        checked={onlyManagersCanShare}
        onIonChange={e => setOnlyManagersCanShare(e.detail.checked)}
        data-testid="only-managers-can-share-checkbox"
      />
      <IonLabel style={{ whiteSpace: 'normal' }}>
        Only listing managers can share
      </IonLabel>
    </IonItem>
  </SectionCard>
);

export default VisibilityLimitsBlock;
